import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { WebPageSchema, BreadcrumbListSchema, ArticleSchema, VideoObjectSchema } from '@/components/base/SchemaOrg';
import { videos } from '@/mocks/videos';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const video = videos.find((v) => v.id === id);

  if (!video) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-error-warning-line text-foreground-300 text-4xl"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">视频未找到</h1>
            <p className="text-foreground-500 mb-6">该视频可能已被删除或链接无效</p>
            <Link to="/" className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedVideos = videos
    .filter((v) => v.id !== video.id && v.category === video.category)
    .slice(0, 4);

  const categoryLabels: Record<string, string> = {
    entertainment: '娱乐搞笑', music: '音乐舞蹈', food: '美食烹饪',
    travel: '旅行户外', tech: '科技数码', sports: '体育运动',
    lifestyle: '生活日常', knowledge: '知识科普', game: '游戏电竞',
  };

  return (
    <>
      <ScrollToTop />
      <title>{`${video.title} - 海角社区视频`}</title>
      <meta name="description" content={video.description.slice(0, 160)} />
      <meta name="keywords" content={`海角社区视频,${video.tags?.join(',') || ''},${video.author.name}`} />
      <link rel="canonical" href={`https://haijiao.shipin.com/article/${video.id}`} />
      <meta property="og:title" content={`${video.title} - 海角社区视频`} />
      <meta property="og:description" content={video.description} />
      <meta property="og:image" content={video.thumbnail} />
      <meta property="og:url" content={`https://haijiao.shipin.com/article/${video.id}`} />
      <meta property="og:type" content="video.other" />
      <meta name="twitter:card" content="summary_large_image" />
      <WebPageSchema
        title={`${video.title} - 海角社区视频`}
        description={video.description}
        url={`https://haijiao.shipin.com/article/${video.id}`}
        datePublished={video.publishDate}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: 'https://haijiao.shipin.com' },
          { name: categoryLabels[video.category] || video.category, url: `https://haijiao.shipin.com/category/${video.category}` },
          { name: video.title, url: `https://haijiao.shipin.com/article/${video.id}` },
        ]}
      />
      <ArticleSchema
        headline={video.title}
        description={video.description}
        image={video.thumbnail}
        datePublished={video.publishDate}
        authorName={video.author.name}
        url={`https://haijiao.shipin.com/article/${video.id}`}
      />
      <VideoObjectSchema
        name={video.title}
        description={video.description}
        thumbnailUrl={video.thumbnail}
        uploadDate={video.publishDate}
        duration={`PT${video.duration.replace(':', 'M')}S`}
        embedUrl={`https://haijiao.shipin.com/article/${video.id}/embed`}
      />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb />
        </div>

        <article className="max-w-5xl mx-auto px-4 md:px-6 pb-14">
          {/* Video Player Area */}
          <div className="relative aspect-video rounded-2xl overflow-hidden bg-foreground-900 mb-6">
            <img
              src={video.thumbnail}
              alt={video.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-foreground-900/30 flex items-center justify-center cursor-pointer group">
              <div className="w-20 h-20 rounded-full bg-primary-500 flex items-center justify-center group-hover:bg-primary-600 group-hover:scale-110 transition-all duration-300 shadow-lg">
                <i className="ri-play-fill text-background-50 text-3xl ml-1"></i>
              </div>
            </div>
            <div className="absolute bottom-4 right-4 bg-foreground-900/75 text-background-50 text-sm px-3 py-1 rounded-lg font-medium">
              {video.duration}
            </div>
          </div>

          {/* Video Info */}
          <div className="mb-6">
            <h1 className="text-xl md:text-2xl font-bold text-foreground-900 mb-3 leading-snug">
              {video.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-4">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {video.views} 次观看
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {video.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-heart-line"></i>
                {video.likes} 点赞
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-chat-3-line"></i>
                {video.comments} 评论
              </span>
            </div>

            {/* Tags */}
            {video.tags && (
              <div className="flex flex-wrap gap-2 mb-5">
                {video.tags.map((tag) => (
                  <span key={tag} className="text-xs text-accent-700 bg-accent-50 px-3 py-1 rounded-full font-medium">
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Author */}
            <div className="flex items-center gap-3 p-4 bg-background-100 rounded-xl">
              <img
                src={video.author.avatar}
                alt={video.author.name}
                className="w-12 h-12 rounded-full object-cover flex-shrink-0"
              />
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground-900">{video.author.name}</p>
                <p className="text-xs text-foreground-400">{video.views} 次观看 · 海角社区视频创作者</p>
              </div>
              <button className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
                + 关注
              </button>
            </div>
          </div>

          {/* Description */}
          <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-10">
            <h3 className="text-sm font-semibold text-foreground-900 mb-3">视频简介</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">{video.description}</p>
          </div>

          {/* Comments Section Placeholder */}
          <div className="bg-background-100 rounded-xl p-6 mb-10">
            <h3 className="text-base font-semibold text-foreground-900 mb-4 flex items-center gap-2">
              <i className="ri-chat-3-line"></i>
              评论 ({video.comments})
            </h3>
            <div className="flex gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-background-300 flex-shrink-0"></div>
              <div className="flex-1">
                <textarea
                  placeholder="发表你的评论..."
                  className="w-full bg-background-50 border border-background-200 rounded-xl p-3 text-sm text-foreground-700 placeholder-foreground-300 focus:outline-none focus:border-primary-300 resize-none"
                  rows={3}
                ></textarea>
                <div className="flex justify-end mt-2">
                  <button className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
                    发布评论
                  </button>
                </div>
              </div>
            </div>
            <div className="text-center py-6 text-foreground-400 text-sm">
              <i className="ri-emotion-happy-line text-xl block mb-2"></i>
              暂无评论，来说两句吧
            </div>
          </div>

          {/* Related Videos */}
          {relatedVideos.length > 0 && (
            <section>
              <h2 className="text-lg font-bold text-foreground-900 mb-5 flex items-center gap-2">
                <i className="ri-stack-line text-accent-500"></i>
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {relatedVideos.map((v) => (
                  <VideoCard key={v.id} {...v} />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}